import { API, Auth } from 'aws-amplify';
import { validateAccessToken } from '@/utility/amplify';
import { setDeviceLimitFlag, isDeviceLimitActive, getDeviceLimitFlag, clearDeviceLimitFlag } from './deviceLimitStorage';

interface DeviceCountData {
  device_count: number;
  is_limit_reached: boolean;
}

interface DeviceCountResponse {
  data: DeviceCountData;
}

export interface DeviceLimitResult {
  blocked: boolean;
  deviceCount: number;
}

const fetchToggle = async (): Promise<string> => {
  try {
    const response = await fetch('/api/config/limit-device-toggle', {
      cache: 'no-store',
    });
    const data = await response.json();
    return data?.toggle || 'off';
  } catch {
    return 'off';
  }
};

export const checkDeviceLimitFromStorage = async (): Promise<DeviceLimitResult | null> => {
  const toggle = await fetchToggle();
  if (toggle !== 'on') {
    clearDeviceLimitFlag();
    return null;
  }

  if (isDeviceLimitActive()) {
    const flag = getDeviceLimitFlag();
    if (flag) {
      return { blocked: true, deviceCount: flag.deviceCount };
    }
  }

  return null;
};

export const checkDeviceLimit = async (): Promise<DeviceLimitResult> => {
  try {
    const toggle = await fetchToggle();
    if (toggle !== 'on') {
      clearDeviceLimitFlag();
      return { blocked: false, deviceCount: 0 };
    }

    await validateAccessToken();

    const extraReq = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getAccessToken()
          .getJwtToken()}`,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0',
      },
      queryStringParameters: {
        _t: Date.now(),
      },
    };

    const response: DeviceCountResponse = await API.get('v1', '/auth/device-count', extraReq);
    const { device_count, is_limit_reached } = response.data;

    if (is_limit_reached === true) {
      setDeviceLimitFlag(device_count);
      return { blocked: true, deviceCount: device_count };
    }

    return { blocked: false, deviceCount: 0 };
  } catch (error) {
    console.error('[DeviceLimit] Failed to check device limit:', error);
    return { blocked: false, deviceCount: 0 };
  }
};
